import { useState, useEffect, useCallback } from 'react';
import formService from '../services/formService';
import toast from 'react-hot-toast';
import useUndoRedo from './useUndoRedo';
import { getUserFriendlyError, ERROR_MESSAGES } from '../utils/errorMessages';

const useFormBuilder = (formId = null) => {
  // Initialize form state with undo/redo support
  const initialFormState = {
    id: formId || null,
    title: 'Untitled Form',
    description: '',
    questions: [],
    settings: {
      visibility: 'public',
      showProgressBar: true,
      allowAnonymous: true,
      gamification: true,
      theme: 'white',
      backgroundStyle: 'solid'
    },
    status: 'draft'
  };

  const {
    state: form,
    set: setFormWithHistory,
    updateWithoutHistory: setFormWithoutHistory,
    undo,
    redo,
    canUndo,
    canRedo,
    reset: resetHistory
  } = useUndoRedo(initialFormState, 50);

  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [isLoading, setIsLoading] = useState(!!formId);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Helper to get quest descriptions
  const getQuestDescription = (questNumber) => {
    const descriptions = {
      1: '', 
      2: '', 
      3: '', 
      4: '', 
      5: '' 
    };
    return descriptions[questNumber] || 'Questions';
  };

  // Calculate quests based on questions
  // Max 3 quests: 1-10 questions = 1 quest, 11-30 = 2 quests, 31-50 = 3 quests
  const calculateQuests = useCallback((questions) => {
    if (!questions || questions.length === 0) return [];

    const totalQuestions = questions.length;
    let numQuests;

    // Determine number of quests based on question count
    if (totalQuestions <= 10) {
      numQuests = 1;
    } else if (totalQuestions <= 30) {
      numQuests = 2;
    } else {
      numQuests = 3; // Max 3 quests for 31-50 questions
    }

    // Calculate questions per quest (distribute evenly)
    const baseQuestionsPerQuest = Math.floor(totalQuestions / numQuests);
    const remainder = totalQuestions % numQuests;

    const quests = [];
    let questionIndex = 0;

    for (let i = 0; i < numQuests; i++) {
      // Add one extra question to earlier quests if there's a remainder
      const questQuestionCount = baseQuestionsPerQuest + (i < remainder ? 1 : 0);
      const questQuestions = questions.slice(questionIndex, questionIndex + questQuestionCount);
      questionIndex += questQuestionCount;

      const questNumber = i + 1;

      quests.push({
        id: `quest_${questNumber}`,
        title: `Quest ${questNumber}`,
        description: getQuestDescription(questNumber),
        illustration: `quest${questNumber}`,
        questions: questQuestions,
        completed: false,
        started: false
      });
    }

    return quests;
  }, []);

  // Get current quests (computed property)
  const quests = calculateQuests(form.questions);

  // Load form on mount if formId is provided
  useEffect(() => {
    if (formId) {
      loadForm();
    }
  }, [formId]);

  // Load form from backend
  const loadForm = async () => {
    setIsLoading(true);
    try {
      const response = await formService.getForm(formId);
      if (response.success) {
        resetHistory(response.data); // Reset history with loaded form
        setHasUnsavedChanges(false); // Reset unsaved changes flag when loading
      }
    } catch (error) {
      const friendlyMessage = getUserFriendlyError(error, ERROR_MESSAGES.FORM.LOAD_FAILED);
      toast.error(friendlyMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Auto-save and auto-draft creation functionality
  useEffect(() => {
    // If form has an ID, auto-save it
    if (form.id) {
      const saveTimer = setTimeout(() => {
        saveForm();
      }, 1000);
      return () => clearTimeout(saveTimer);
    }

    // If no form ID but has content, auto-create as draft
    const hasContent = form.title !== 'Untitled Form' ||
                      form.description !== '' ||
                      form.questions.length > 0;

    if (hasContent && !form.id) {
      const draftTimer = setTimeout(() => {
        createDraft();
      }, 2000); // Wait 2 seconds before creating draft
      return () => clearTimeout(draftTimer);
    }
  }, [form]);

  // Create draft automatically
  const createDraft = async () => {
    if (form.id) return; // Don't create if already exists

    try {
      const response = await formService.createForm({
        title: form.title,
        description: form.description,
        questions: form.questions,
        settings: form.settings
      });

      if (response.success) {
        // Update form ID without creating history entry
        setFormWithoutHistory({
          ...form,
          id: response.data.id
        });
        setLastSaved(new Date());
        // Don't show toast for auto-draft creation to avoid interrupting user
      }
    } catch (error) {
      console.error('Failed to auto-create draft:', error);
    }
  };

  // Save form to backend
  const saveForm = useCallback(async () => {
    if (!form.id) return; // Don't save if form hasn't been created yet

    setIsSaving(true);

    try {
      const formData = {
        title: form.title,
        description: form.description,
        questions: form.questions,
        settings: form.settings
      };

      const response = await formService.updateForm(form.id, formData);

      if (response.success) {
        setLastSaved(new Date());
        // Mark as having unsaved changes if form is published (changes need republishing)
        if (form.status === 'published') {
          setHasUnsavedChanges(true);
        }
      }
    } catch (error) {
      console.error('Failed to save form:', error);
    } finally {
      setIsSaving(false);
    }
  }, [form]);

  // Update form-level properties
  const updateForm = useCallback((field, value) => {
    setFormWithHistory({
      ...form,
      [field]: value
    });
  }, [form, setFormWithHistory]);

  // Update form settings
  const updateFormSetting = useCallback((setting, value) => {
    setFormWithHistory({
      ...form,
      settings: {
        ...form.settings,
        [setting]: value
      }
    });
  }, [form, setFormWithHistory]);

  // Add new question
  const addQuestion = useCallback((type) => {
    // Determine default options based on question type
    let defaultOptions = [];

    if (type === 'multiple-choice' || type === 'checkbox' || type === 'dropdown' || type === 'ranking') {
      defaultOptions = ['Option 1', 'Option 2', 'Option 3'];
    } else if (type === 'image') {
      defaultOptions = [];
    }

    const newQuestion = {
      id: `q_${Date.now()}`,
      type: type,
      text: '',
      required: false,
      points: 0,
      animation: 'fade-in',
      options: defaultOptions
    };

    setFormWithHistory({
      ...form,
      questions: [...form.questions, newQuestion]
    });

    setSelectedQuestionId(newQuestion.id);
  }, [form, setFormWithHistory]);

  // Update question
  const updateQuestion = useCallback((questionId, updates) => {
    setFormWithHistory({
      ...form,
      questions: form.questions.map(q =>
        q.id === questionId ? { ...q, ...updates } : q
      )
    });
  }, [form, setFormWithHistory]);

  // Update question options (for multiple choice)
  const updateQuestionOptions = useCallback((questionId, options) => {
    setFormWithHistory({
      ...form,
      questions: form.questions.map(q =>
        q.id === questionId ? { ...q, options } : q
      )
    });
  }, [form, setFormWithHistory]);

  // Delete question
  const deleteQuestion = useCallback((questionId) => {
    setFormWithHistory({
      ...form,
      questions: form.questions.filter(q => q.id !== questionId)
    });

    if (selectedQuestionId === questionId) {
      setSelectedQuestionId(null);
    }
  }, [form, selectedQuestionId, setFormWithHistory]);

  // Duplicate question
  const duplicateQuestion = useCallback((questionId) => {
    const questionToDuplicate = form.questions.find(q => q.id === questionId);

    if (questionToDuplicate) {
      const newQuestion = {
        ...questionToDuplicate,
        id: `q_${Date.now()}`,
        text: `${questionToDuplicate.text} (Copy)`
      };

      const questionIndex = form.questions.findIndex(q => q.id === questionId);
      const newQuestions = [...form.questions];
      newQuestions.splice(questionIndex + 1, 0, newQuestion);

      setFormWithHistory({
        ...form,
        questions: newQuestions
      });
    }
  }, [form, setFormWithHistory]);

  // Reorder questions with proper drag and drop
  const reorderQuestions = useCallback((startIndex, endIndex) => {
    const result = Array.from(form.questions);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);

    setFormWithHistory({
      ...form,
      questions: result
    });
  }, [form, setFormWithHistory]);

  // Get selected question
  const selectedQuestion = form.questions.find(q => q.id === selectedQuestionId);

  // Create new form in backend
  const createNewForm = async () => {
    try {
      const response = await formService.createForm({
        title: form.title,
        description: form.description,
        questions: form.questions,
        settings: form.settings
      });

      if (response.success) {
        // Update form without creating history entry (backend operation)
        setFormWithoutHistory(response.data);
        toast.success('Form created successfully');
        return response.data;
      }
    } catch (error) {
      const friendlyMessage = getUserFriendlyError(error, ERROR_MESSAGES.FORM.SAVE_FAILED);
      toast.error(friendlyMessage);
      throw error;
    }
  };

  // Publish form
  const publishForm = async (formIdToPublish = null) => {
    const idToUse = formIdToPublish || form.id;

    if (!idToUse) {
      toast.error(ERROR_MESSAGES.FORM.EMPTY_TITLE);
      return;
    }

    if (!form.questions || form.questions.length === 0) {
      toast.error(ERROR_MESSAGES.FORM.NO_QUESTIONS);
      return;
    }

    setIsPublishing(true);
    try {
      const response = await formService.publishForm(idToUse);

      if (response.success) {
        // Update form without creating history entry (backend operation)
        setFormWithoutHistory(response.data);
        setHasUnsavedChanges(false); // Clear unsaved changes flag after publishing
        toast.success('Form published successfully!');
        return response.data;
      }
    } catch (error) {
      const friendlyMessage = getUserFriendlyError(error, ERROR_MESSAGES.FORM.PUBLISH_FAILED);
      toast.error(friendlyMessage);
      throw error;
    } finally {
      setIsPublishing(false);
    }
  };

  // Import multiple questions from AI
  const importQuestions = useCallback((questionsArray) => {
    if (!questionsArray || questionsArray.length === 0) {
      return;
    }

    // Generate unique IDs for imported questions
    const questionsWithIds = questionsArray.map((q, index) => ({
      ...q,
      id: `q_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      animation: q.animation || 'fade-in'
    }));

    // Append to existing questions (single undo action for all imported questions)
    setFormWithHistory({
      ...form,
      questions: [...form.questions, ...questionsWithIds]
    });

    // Auto-select first imported question
    if (questionsWithIds.length > 0) {
      setSelectedQuestionId(questionsWithIds[0].id);
    }

    setHasUnsavedChanges(true);
  }, [form, setFormWithHistory]);

  // Delete all questions
  const deleteAllQuestions = useCallback(() => {
    setFormWithHistory({
      ...form,
      questions: []
    });

    // Reset selected question
    setSelectedQuestionId(null);

    toast.success('All questions deleted');
  }, [form, setFormWithHistory]);

  return {
    form,
    quests,
    selectedQuestion,
    selectedQuestionId,
    isSaving,
    isPublishing,
    lastSaved,
    isLoading,
    hasUnsavedChanges,
    updateForm,
    updateFormSetting,
    addQuestion,
    updateQuestion,
    updateQuestionOptions,
    deleteQuestion,
    duplicateQuestion,
    reorderQuestions,
    deleteAllQuestions,
    setSelectedQuestionId,
    saveForm,
    createNewForm,
    publishForm,
    importQuestions,
    undo,
    redo,
    canUndo,
    canRedo
  };
};

export default useFormBuilder;